import React, { forwardRef, useMemo } from 'react';

import PropTypes from 'prop-types';

import { TreeGuidelineTokens, useTheme } from '@adsk/alloy-react-styles';
import {
  stylePropType,
  StylableComponent,
  StyleProp,
} from '@adsk/alloy-react-theme';

import { INDENT_UNIT } from '../consts';

type IndentType = 'Ancestor' | 'Parent' | 'Self';

const getGuideLineStyle = ({
  type,
  isExpandable,
  isLastAtDepth,
  guideline,
}: {
  type: IndentType;
  isExpandable: boolean;
  isLastAtDepth?: boolean;
  guideline?: TreeGuidelineTokens;
}): StyleProp => {
  if (type === 'Self') {
    return {};
  }

  const styles: Record<string, unknown> = {
    '&::after': {
      borderLeftWidth: guideline?.['border-width'],
      borderLeftStyle: guideline?.['border-style'],
      borderLeftColor: guideline?.['border-color'],
      opacity: guideline?.opacity,
      display: 'inline-block',
      content: `""`,
      height: type === 'Parent' && isLastAtDepth ? '50%' : '100%',
      left: INDENT_UNIT / 2,
      position: 'absolute',
      top: 0,
    },
  };

  if (type === 'Parent') {
    styles['&::before'] = {
      borderTopWidth: guideline?.['border-width'],
      borderTopStyle: guideline?.['border-style'],
      borderTopColor: guideline?.['border-color'],
      opacity: guideline?.opacity,
      display: 'inline-block',
      content: `""`,
      left: INDENT_UNIT / 2,
      margin: 0,
      position: 'absolute',
      top: 20,
      width: isExpandable ? INDENT_UNIT / 3 : INDENT_UNIT,
    };
  }

  return styles as StyleProp;
};

type IndentProps = StylableComponent<HTMLDivElement> & {
  guidelines?: boolean;
  /** Current depth of this node, which offsets the node to the right to indicate nesting */
  depth?: number;
  isExpandable: boolean;
  isLastAtDepth?: boolean;
  nodeId: string;
};

const getIndentTypes = ({
  depth,
  isExpandable,
}: {
  depth: number;
  isExpandable: boolean;
}): { type: IndentType; position: number }[] => {
  const indentDepth = isExpandable ? depth : (depth ?? 0) + 1;

  return [...Array(indentDepth).keys()].map((_, i, arr) => {
    let type: IndentType = 'Ancestor';
    if (i === arr.length - 1) {
      type = isExpandable ? 'Parent' : 'Self';
    } else if (i === arr.length - 2) {
      type = isExpandable ? 'Ancestor' : 'Parent';
    }
    return { type, position: i };
  });
};

const baseIndentStyle: StyleProp = {
  position: 'relative',
  minWidth: INDENT_UNIT,
  width: INDENT_UNIT,
  height: '100%',
};

const Indent = forwardRef<HTMLDivElement, IndentProps>(
  (
    {
      guidelines = true,
      depth = 0,
      nodeId,
      isExpandable,
      isLastAtDepth,
      ...props
    },
    ref,
  ) => {
    const { tokens } = useTheme();
    const guideline = tokens?.component.tree?.indent?.guideline;

    const indentTypes = useMemo(
      () => getIndentTypes({ depth, isExpandable }),
      [depth, isExpandable],
    );

    const { style, ...restProps } = props;

    return (
      <>
        {indentTypes.map(({ type, position }) =>
          guidelines ? (
            <div
              ref={ref}
              data-testid="TreeNodeIndent"
              className={type}
              key={`${nodeId}${position}`}
              css={[
                baseIndentStyle,
                getGuideLineStyle({
                  type,
                  isExpandable,
                  isLastAtDepth,
                  guideline,
                }),
                style,
              ]}
              {...(restProps as React.HTMLAttributes<HTMLDivElement>)}
            />
          ) : (
            <div
              ref={ref}
              data-testid="TreeNodeIndent"
              className={type}
              key={`${nodeId}${position}`}
              css={[baseIndentStyle, style]}
              {...(restProps as React.HTMLAttributes<HTMLDivElement>)}
            />
          ),
        )}
      </>
    );
  },
);

Indent.displayName = 'Indent';

Indent.propTypes = {
  /** style object to add to the wrapper of the node */
  style: stylePropType,
  /** className to add to the wrapper of the node */
  className: PropTypes.string,
  /** Current depth of this node, which offsets the node to the right to indicate nesting */
  depth: PropTypes.number,
};

export default Indent;
